import { QueryClient, QueryKey } from "@tanstack/react-query"
import { FetchThreadsResponse, Thread } from "../types/thread"

type ThreadsCacheData = FetchThreadsResponse | undefined

/**
 * A row as a writer builds it: pi-server hands back the id and timestamp as plain strings, so the write path
 * can't produce the `Uuid`/`IsoDate` brands `Thread` reads them back as.
 */
export type NewThread = Omit<Thread, "threadId" | "updatedAt"> & {
  threadId: string
  updatedAt: string
}

/**
 * Generate a React Query cache key for AI teammate threads
 */
export function getThreadsQueryKey(accountAlias: string): [string, string] {
  return ["ai-teammate-threads", accountAlias]
}

/**
 * Upsert a thread into the React Query cache by threadId. Idempotent: an early `thread_created` write and the
 * later `finish` write for the same turn produce one row, not two — a re-add field-merges in place (e.g. an
 * updated title/updatedAt) rather than prepending a duplicate. A genuinely new id is prepended (newest first).
 */
export function addThreadToCache(queryClient: QueryClient, queryKey: QueryKey, newThread: NewThread): void {
  queryClient.setQueryData<FetchThreadsResponse>(queryKey, (oldData: ThreadsCacheData) => {
    const oldThreads = oldData?.threads || []
    const existing = oldThreads.some((thread) => thread.threadId === newThread.threadId)
    // The single place the server's plain strings take on the read-side brands.
    const branded = newThread as Thread
    const threads = existing
      // `pinned` is owned by the pin mutation and the server fetch — a creation write only supplies it as the
      // default for a NEW row, and must not undo a pin the user applied between the two writes.
      ? oldThreads.map((thread) => (thread.threadId === branded.threadId
        ? { ...thread, ...branded, ...(thread.pinned != null && { pinned: thread.pinned }) }
        : thread))
      : [branded, ...oldThreads]
    return { ...oldData, threads }
  })
}

/**
 * Field-preserving patch of one thread in the shared threads cache (both chat surfaces write here).
 * Spreads `patch` over the matched thread, so a `pinned` write can't drop `title` and vice-versa.
 */
export function updateThreadInCache(
  queryClient: QueryClient,
  queryKey: QueryKey,
  threadId: string,
  patch: Partial<Thread>,
): void {
  queryClient.setQueryData<FetchThreadsResponse>(queryKey, (oldData: ThreadsCacheData) => {
    if (!oldData?.threads) return oldData
    return {
      ...oldData,
      threads: oldData.threads.map((thread) => (thread.threadId === threadId ? { ...thread, ...patch } : thread)),
    }
  })
}

/**
 * Drop one thread from the shared threads cache (the optimistic half of delete).
 */
export function removeThreadFromCache(queryClient: QueryClient, queryKey: QueryKey, threadId: string): void {
  queryClient.setQueryData<FetchThreadsResponse>(queryKey, (oldData: ThreadsCacheData) => {
    if (!oldData?.threads) return oldData
    return {
      ...oldData,
      threads: oldData.threads.filter((thread) => thread.threadId !== threadId),
    }
  })
}

/**
 * Put one thread back at `index` (the rollback half of delete). Splices into the CURRENT list rather than
 * restoring a snapshot, so a pin/rename/new thread landing during the delete isn't rewound; a no-op if the
 * thread is already back (a refetch beat the rollback).
 */
export function restoreThreadToCache(
  queryClient: QueryClient,
  queryKey: QueryKey,
  thread: Thread,
  index: number,
): void {
  queryClient.setQueryData<FetchThreadsResponse>(queryKey, (oldData: ThreadsCacheData) => {
    if (!oldData?.threads) return oldData
    if (oldData.threads.some((candidate) => candidate.threadId === thread.threadId)) return oldData
    const threads = [...oldData.threads]
    threads.splice(Math.min(Math.max(index, 0), threads.length), 0, thread)
    return { ...oldData, threads }
  })
}
